import React, { useState } from "react";
import { Siren, Check, ShieldCheck, RefreshCw } from "lucide-react";
import { Button } from "../shared/Button";
import { sos as api } from "../../api/modulos.service";
import { estilos as est, Cabecera, Vacio, Aviso, useCarga } from "./base";

/**
 * BOTÓN DE EMERGENCIA.
 *
 * Para el RESIDENTE: un botón grande y nada más. No pide motivo, ni
 * confirmación, ni un formulario: quien lo toca no está en condiciones de
 * llenar campos. Todo lo demás lo pone el servidor.
 *
 * Para la CASETA: la bandeja de alertas activas, con «voy para allá» y
 * «cerrar». El tiempo entre una cosa y otra es la métrica que importa.
 */

const fecha = (f) =>
  new Date(f).toLocaleString("es-SV", { timeZone: "America/El_Salvador", dateStyle: "short", timeStyle: "short" });

export default function Sos({ rol, modulos }) {
  const [enviando, setEnviando] = useState(false);
  const [aviso, setAviso] = useState("");
  const [error, setError] = useState("");

  const { datos, cargando, error: errorCarga, recargar } = useCarga(() => api.listar({}), []);
  const activas = datos?.alerts || [];
  const esCaseta = rol === "guard" || rol === "admin";

  React.useEffect(() => {
    if (!esCaseta) return undefined;
    const t = setInterval(recargar, 20000);
    return () => clearInterval(t);
  }, [esCaseta, recargar]);

  if (modulos && modulos.sos === false) {
    return (
      <div style={est.seccion}>
        <Cabecera titulo="Emergencia" icono={Siren} />
        <Vacio>Su residencial tiene desactivado el botón de emergencia.</Vacio>
      </div>
    );
  }

  const activar = async () => {
    setError(""); setEnviando(true);
    try {
      const r = await api.activar({});
      setAviso(r.repetida ? "Su alerta ya estaba activa. Seguridad va en camino." : "Alerta enviada a seguridad.");
      recargar();
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudo enviar la alerta");
    } finally { setEnviando(false); }
  };

  return (
    <div style={est.seccion}>
      <Cabecera
        titulo="Emergencia"
        subtitulo={esCaseta ? "Alertas activas de los residentes." : "Si necesita ayuda, toque el botón."}
        icono={Siren}
      />

      {error && <Aviso>{error}</Aviso>}
      {aviso && <Aviso>{aviso}</Aviso>}

      {rol === "resident" && (
        <button
          type="button"
          onClick={activar}
          disabled={enviando}
          style={{
            // Grande y sin adornos: se toca con la mano temblando.
            width: "100%",
            minHeight: "160px",
            borderRadius: "var(--radius-xl)",
            border: "4px solid var(--danger)",
            backgroundColor: "var(--danger)",
            color: "#fff",
            fontSize: "var(--text-2xl)",
            fontWeight: 800,
            letterSpacing: "0.08em",
            cursor: "pointer",
            display: "grid",
            placeItems: "center",
            gap: "var(--space-3)",
          }}
        >
          <Siren size={48} />
          {enviando ? "ENVIANDO…" : "EMERGENCIA"}
        </button>
      )}

      {esCaseta && (
        errorCarga ? (
          <Aviso>
            {errorCarga}
            <div style={{ marginTop: "var(--space-3)" }}>
              <Button size="sm" variant="outline" leftIcon={RefreshCw} onClick={recargar}>
                Reintentar
              </Button>
            </div>
          </Aviso>
        ) : cargando ? (
          <Vacio>Cargando…</Vacio>
        ) : activas.length === 0 ? (
          /* «No hay emergencias activas» sólo se dice cuando de verdad se
             pudo preguntar: aquí decirlo por error sería tranquilizar sin
             motivo. */
          <Vacio>No hay emergencias activas.</Vacio>
        ) : (
          <div style={est.rejilla}>
            {activas.map((a) => (
              <div key={a._id} style={{ ...est.tarjeta, borderColor: "var(--danger)", borderWidth: "2px" }}>
                <h3 style={{ ...est.tarjetaTitulo, color: "var(--danger)" }}>
                  🆘 {a.raiserName}
                </h3>
                <div style={est.tarjetaDato}>Casa {a.polygon}-{a.houseNumber}</div>
                {a.phone && <div style={est.tarjetaDato}>Teléfono {a.phone}</div>}
                <div style={est.tarjetaDato}>{fecha(a.createdAt)}</div>
                {a.acknowledgedAt && (
                  <div style={{ ...est.tarjetaDato, color: "var(--success)" }}>
                    Atendida por {a.acknowledgedBy?.name} a las {fecha(a.acknowledgedAt)}
                  </div>
                )}
                <div style={est.tarjetaAcciones}>
                  {!a.acknowledgedAt && (
                    <Button size="sm" variant="primary" leftIcon={ShieldCheck}
                      onClick={() => api.atender(a._id).then(recargar)}>
                      Voy para allá
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" leftIcon={Check}
                    onClick={() => {
                      const r = window.prompt("¿Cómo se resolvió?");
                      api.cerrar(a._id, { resolution: r || "" }).then(recargar);
                    }}>
                    Cerrar
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
